import { FC, useState } from "react";
import { motion } from "framer-motion";
import styles from "./Avatar.module.scss";
import defaultAvatar from "@assets/lottie/default_avatar.json";
import { Player } from "@lottiefiles/react-lottie-player";

interface IAvatarProps {
  src: string;
  dominantColors?: string[];
  showPulseEffect?: boolean;
}

export const Avatar: FC<IAvatarProps> = (props) => {
  const { src, dominantColors = [], showPulseEffect = false } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const defaultColors = ["#FFD700", "#FFA500", "#FF8C00", "#DAA520", "#B8860B"];

  const shouldShowLoading = src && isLoading && !hasError;
  const shouldShowDefaultAvatar = !src || src === "" || hasError;

  const effectiveColors =
    dominantColors.length > 0 ? dominantColors : defaultColors;

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className={styles.avatarContainer}>
      {showPulseEffect && effectiveColors.length > 0 && (
        <>
          <motion.div
            className={styles.pulsePrimary}
            initial={{ opacity: 0.6, scale: 0.9 }}
            animate={{
              opacity: [0.5, 0.7, 0.5],
              scale: [1, 1.05, 1],
              rotate: [0, 2, 0],
              y: [-2, 5, -2],
              x: [-2, 2, -2],
            }}
            transition={{
              duration: 3.5,
              ease: "easeInOut",
              repeat: Infinity,
              times: [0, 0.5, 1],
            }}
            style={{
              backgroundColor: effectiveColors[0],
              willChange: "opacity, transform",
            }}
          />
          {effectiveColors.length > 1 && (
            <motion.div
              className={styles.pulseSecondary}
              initial={{ opacity: 0.35, scale: 1 }}
              animate={{
                opacity: [0.2, 0.35, 0.2],
                scale: [1, 1.08, 1],
                rotate: [0, -2, 0],
              }}
              transition={{
                duration: 2,
                ease: "easeInOut",
                repeat: Infinity,
                delay: 0.2,
              }}
              style={{
                willChange: "opacity, transform",
                backgroundColor: effectiveColors[1],
              }}
            />
          )}
          {effectiveColors.length > 2 && (
            <motion.div
              className={styles.pulseTertiary}
              initial={{ opacity: 0.3, scale: 1 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.06, 1],
                rotate: [0, 1.5, 0],
              }}
              transition={{
                duration: 2.8,
                ease: "easeInOut",
                repeat: Infinity,
                delay: 0.4,
              }}
              style={{
                willChange: "opacity, transform",
                backgroundColor: effectiveColors[2],
              }}
            />
          )}
          {effectiveColors.length > 3 && (
            <motion.div
              className={styles.pulseQuaternary}
              initial={{ opacity: 0.25, scale: 1 }}
              animate={{
                opacity: [0.1, 0.25, 0.1],
                scale: [1, 1.04, 1],
                rotate: [0, -1.5, 0],
              }}
              transition={{
                duration: 3.2,
                ease: "easeInOut",
                repeat: Infinity,
                delay: 0.6,
              }}
              style={{
                willChange: "opacity, transform",
                backgroundColor: effectiveColors[3],
              }}
            />
          )}
          {effectiveColors.length > 4 && (
            <motion.div
              className={styles.pulseQuinary}
              initial={{ opacity: 0.2, scale: 1 }}
              animate={{
                opacity: [0.05, 0.2, 0.05],
                scale: [1, 1.03, 1],
                rotate: [0, 1, 0],
              }}
              transition={{
                duration: 3.8,
                ease: "easeInOut",
                repeat: Infinity,
                delay: 0.8,
              }}
              style={{
                willChange: "opacity, transform",
                backgroundColor: effectiveColors[4],
              }}
            />
          )}
        </>
      )}
      <motion.div
        className={styles.avatarImageWrapper}
        style={{
          willChange: "opacity, transform",
          boxShadow:
            effectiveColors.length > 0 && showPulseEffect
              ? `0 0 10px 0 ${effectiveColors[0]}50, inset 0 0 0 2px ${effectiveColors[0]}30`
              : undefined,
        }}
      >
        {shouldShowLoading && <div className={styles.skeleton} />}
        {shouldShowDefaultAvatar ? (
          <Player autoplay loop src={defaultAvatar} />
        ) : (
          <img
            src={src}
            alt="profile image"
            className={styles.avatar}
            loading="eager"
            fetchPriority="high"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
      </motion.div>
    </div>
  );
};
